import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "antd";

/**
 * SmartBarcodeScanner (iOS-ready tone + audio unlock)
 * - При клике на "Сканувати" сразу играет короткий ready-тон (без await) → надёжный unlock на iOS
 * - iOS 17+: navigator.audioSession.type='playback' (звук не глушится тихим режимом)
 * - iOS: теперь приоритет — playBeep (WebAudio осциллятор из клика), HTMLAudio оставлен как fallback
 * - Другие платформы: WebAudio-осциллятор
 * - Детектор: BarcodeDetector → ZXing fallback
 * - Torch/AF/ROI/вибро: сохранены
 */

export default function SmartBarcodeScanner({
  onScan,
  height = 260,
  frameIntervalMs = 120,        // 80–150ms даёт «живое» сканирование
  dedupeWindowMs = 1000,        // подавление дублей одного и того же кода
  // 🔊 индикация звуком
  beep = true,
  beepFreq = 880,
  beepDurationMs,               // дефолт см. ниже
  beepVolume,                   // дефолт см. ниже
}) {
  // -------- Платформенные флаги --------
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isMacDesktop = /Macintosh|MacIntel|MacPPC|Mac68K/.test(ua) && !isIOS;

  const BEEP_VOL = beepVolume ?? (isMacDesktop ? 0.15 : 0.07);
  const BEEP_DUR = beepDurationMs ?? (isMacDesktop ? 180 : 120);

  // -------- Refs & state --------
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const trackRef  = useRef(null);

  const rVFCHandleRef = useRef(0);
  const rafHandleRef  = useRef(0);
  const loopActiveRef = useRef(false);

  const canvasRef = useRef(
    typeof document !== "undefined" ? document.createElement("canvas") : null
  );
  const ctxRef    = useRef(null);

  const detectorRef = useRef(null); // BarcodeDetector
  const zxingRef    = useRef(null); // ZXing { reader, HTMLCanvasElementLuminanceSource, HybridBinarizer, BinaryBitmap }
  const modeRef     = useRef("none"); // "native" | "zxing" | "none"

  const [active, setActive] = useState(false);
  const [error,  setError]  = useState(null);
  const [needsGesture, setNeedsGesture] = useState(false);

  const [torchCapable, setTorchCapable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  const [lastCode, setLastCode] = useState("");
  const [fires, setFires] = useState(0);
  const [log, setLog] = useState([]);

  const lastAnyTsRef = useRef(0);
  const lastByCodeRef = useRef(new Map());

  // -------- Audio ----------
  const audioCtxRef = useRef(null);         // WebAudio для non-iOS
  const iosReadyElRef = useRef(null);       // persistent <audio> для iOS (ready-тон) — Fallback
  const iosBeepElRef  = useRef(null);       // persistent <audio> для iOS (бип) — Fallback
  const lastBeepTsRef = useRef(0);

  // ✅ iOS: основной путь — один AudioContext из клика (play-beep как в "том проекте")
  const clickAudioCtxRef = useRef(null);

  // Короткий data:audio (mp3 ~100мс). Можно заменить на свой файл/URI.
  const READY_DATA_URI =
    "data:audio/mp3;base64,//uQZAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCA//////////////////////////////////////////////////////////////////////////////////////////////////////////8AAA==";
  const BEEP_DATA_URI =
    "data:audio/mp3;base64,//uQZAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCA//////////////////////////////////////////////////////////////////////////////////////////////////////////8AAA==";

  // iOS 17+: выставляем тип сессии, чтобы не глушился тихим режимом
  const setPlaybackSession = () => {
    try {
      if (typeof navigator !== "undefined" && navigator.audioSession) {
        navigator.audioSession.type = "playback";
      }
    } catch {}
  };

  // === iOS play-beep (осциллятор) — основной метод для iPhone ===
  const playBeepIOS = (type = "start") => {
    if (!beep) return;
    try { if (navigator.audioSession) navigator.audioSession.type = "playback"; } catch {}
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!clickAudioCtxRef.current) {
      try { clickAudioCtxRef.current = new AC({ latencyHint: "interactive" }); }
      catch { return; }
    }
    const ctx = clickAudioCtxRef.current;
    if (ctx.state === "suspended") { try { ctx.resume(); } catch {} }
    try {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      const freq = type === "start" ? 1000 : 600;
      const vol  = Math.max(0.001, Math.min(1, (beepVolume ?? 0.25)));
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      gain.gain.setValueAtTime(vol, ctx.currentTime);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.06); // ~60мс
      osc.onended = () => { try { osc.disconnect(); gain.disconnect(); } catch {} };
    } catch {}
  };

  // Создание/подготовка persistent <audio> для iOS (резервный путь)
  const ensureIOSAudioEls = () => {
    try {
      if (!iosReadyElRef.current) {
        const a = new Audio(READY_DATA_URI);
        a.preload = "auto";
        iosReadyElRef.current = a;
      }
    } catch {}
    try {
      if (!iosBeepElRef.current) {
        const a = new Audio(BEEP_DATA_URI);
        a.preload = "auto";
        iosBeepElRef.current = a;
      }
    } catch {}
  };

  // Сразу при клике: короткий ready-тон (надёжная привязка к gesture)
  const playReadyTone = () => {
    if (!beep) return;

    setPlaybackSession(); // iOS 17+ (до любых await)

    if (isIOS) {
      // ✅ приоритет — play-beep из клика через осциллятор
      playBeepIOS("start");

      // fallback на случай, если осциллятор не разрешён
      try {
        ensureIOSAudioEls();
        const a = iosReadyElRef.current;
        if (a) {
          a.currentTime = 0;
          a.volume = Math.max(0, Math.min(1, (beepVolume ?? 0.35)));
          a.play().catch(() => {});
        }
      } catch {}
      return;
    }

    // Остальные платформы — WebAudio-«пик» ~80мс
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AC({ latencyHint: "interactive" });
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") { try { ctx.resume(); } catch {} }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(beepVolume ?? 0.08, ctx.currentTime);
      osc.type = "sine";
      osc.frequency.setValueAtTime(1000, ctx.currentTime);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.08);
      osc.onended = () => { try { osc.disconnect(); gain.disconnect(); } catch {} };
    } catch {}
  };

  // Бип при детекции
  const doBeep = useCallback(async () => {
    if (!beep) return;
    const now = Date.now();
    if (now - lastBeepTsRef.current < 300) return; // не чаще раза в 300мс
    lastBeepTsRef.current = now;

    setPlaybackSession();

    if (isIOS) {
      // ✅ основной путь — тот же контекст из клика
      playBeepIOS("hit");

      // fallback HTMLAudio на всякий случай
      try {
        ensureIOSAudioEls();
        const a = iosBeepElRef.current;
        if (a) {
          a.currentTime = 0;
          a.volume = Math.max(0, Math.min(1, (beepVolume ?? 0.4)));
          a.play().catch(() => {});
        }
      } catch {}
      return;
    }

    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AC({ latencyHint: "interactive" });
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") { try { await ctx.resume(); } catch {} }

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(beepFreq, ctx.currentTime);
      gain.gain.setValueAtTime(BEEP_VOL, ctx.currentTime);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + (BEEP_DUR / 1000));
      osc.onended = () => { try { osc.disconnect(); gain.disconnect(); } catch {} };
    } catch {}
  }, [beep, isIOS, beepVolume, beepFreq, BEEP_VOL, BEEP_DUR]);

  // -------- Emit кода --------
  const emit = (text) => {
    const now = Date.now();

    // throttling
    if (now - lastAnyTsRef.current < frameIntervalMs) return;
    lastAnyTsRef.current = now;

    // dedupe per code
    const prev = lastByCodeRef.current.get(text) || 0;
    if (now - prev < dedupeWindowMs) return;
    lastByCodeRef.current.set(text, now);

    setLastCode(text);
    setLog((prev) => [{ text, ts: now }, ...prev].slice(0, 10));

    try { onScan && onScan(text); } catch (e) {
      setError(`onScan error: ${e?.message || e}`);
    }

    // лёгкая вибрация — если доступна
    try {
      if (!isMacDesktop) navigator.vibrate?.(30);
    } catch {}

    doBeep();
    setFires((v) => v + 1);
  };

  // -------- Петля детекции --------
  const cancelFrameLoop = () => {
    loopActiveRef.current = false;
    try {
      if (videoRef.current?.cancelVideoFrameCallback && rVFCHandleRef.current) {
        videoRef.current.cancelVideoFrameCallback(rVFCHandleRef.current);
      }
    } catch {}
    try { if (rafHandleRef.current) cancelAnimationFrame(rafHandleRef.current); } catch {}
    rVFCHandleRef.current = 0;
    rafHandleRef.current = 0;
  };

  // Native: detect прямо с <video>
  const detectNative = async () => {
    const det   = detectorRef.current;
    const video = videoRef.current;
    if (!det || !video) return;
    if (video.readyState < 2) return; // HAVE_CURRENT_DATA
    try {
      const res = await det.detect(video);
      for (const b of res || []) {
        if (b?.rawValue) emit(String(b.rawValue).trim());
      }
    } catch {}
  };

  // Canvas-ROI для ZXing
  const drawROIToCanvas = () => {
    const video = videoRef.current;
    if (!video) return false;
    const vw = video.videoWidth || 0;
    const vh = video.videoHeight || 0;
    if (!vw || !vh) return false;

    // Центральный ROI: 80% ширины, 40% высоты
    const roiW = Math.floor(vw * 0.8);
    const roiH = Math.floor(vh * 0.4);
    const sx   = Math.floor((vw - roiW) / 2);
    const sy   = Math.floor((vh - roiH) / 2);

    const cw = 640;
    const ch = Math.max(1, Math.floor((cw * roiH) / roiW));
    if (canvasRef.current && !ctxRef.current) {
      canvasRef.current.width  = cw;
      canvasRef.current.height = ch;
      ctxRef.current = canvasRef.current.getContext("2d", { willReadFrequently: true });
    }
    const ctx = ctxRef.current;
    if (!ctx || !canvasRef.current) return false;

    ctx.drawImage(video, sx, sy, roiW, roiH, 0, 0, cw, ch);
    return true;
  };

  const detectZXing = async () => {
    const z = zxingRef.current;
    if (!z?.reader) return;
    if (!drawROIToCanvas()) return;
    try {
      const Luma = z.HTMLCanvasElementLuminanceSource;
      const Bin  = z.HybridBinarizer;
      const BMP  = z.BinaryBitmap;

      const src = new Luma(canvasRef.current);
      const bmp = new BMP(new Bin(src));
      const result = z.reader.decode(bmp);
      const text = result?.getText?.();
      if (text) emit(String(text).trim());
    } catch {
      // NotFoundException — норма
    }
  };

  const startDetectLoop = () => {
    const tick = async () => {
      if (!loopActiveRef.current) return;

      if (modeRef.current === "native") await detectNative();
      else if (modeRef.current === "zxing") await detectZXing();

      const schedule = () => {
        if (!loopActiveRef.current) return;
        if (videoRef.current?.requestVideoFrameCallback) {
          rVFCHandleRef.current = videoRef.current.requestVideoFrameCallback(() => {
            setTimeout(() => tick(), frameIntervalMs);
          });
        } else {
          rafHandleRef.current = requestAnimationFrame(() => {
            setTimeout(() => tick(), frameIntervalMs);
          });
        }
      };
      schedule();
    };

    loopActiveRef.current = true;
    tick();
  };

  // -------- Фокус / Torch --------
  const applyTrackControls = async (track) => {
    try {
      const caps = track.getCapabilities?.() || {};
      if (caps.focusMode?.includes?.("continuous")) {
        await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
      } else if (caps.focusMode?.includes?.("auto")) {
        await track.applyConstraints({ advanced: [{ focusMode: "auto" }] });
      }
    } catch {}
  };

  // подkick автофокуса раз в 2с
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      const track = trackRef.current;
      try {
        const caps = track?.getCapabilities?.() || {};
        if (caps.focusMode?.includes?.("continuous")) {
          track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
        }
      } catch {}
    }, 2000);
    return () => clearInterval(id);
  }, [active]);

  // На Android: подобрать заднюю с torch
  const getRearStreamWithTorch = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter(d => d.kind === "videoinput");
      const ordered = [
        ...cams.filter(c => /back|rear/i.test(c.label)),
        ...cams.filter(c => !/back|rear/i.test(c.label)),
      ];

      for (const cam of ordered) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: { deviceId: { exact: cam.deviceId }, facingMode: "environment" },
          });
          const track = stream.getVideoTracks()[0];

          let hasTorch = false;
          const caps = track.getCapabilities?.() || {};
          if (caps.torch) hasTorch = true;

          if (!hasTorch && "ImageCapture" in window) {
            try {
              const ic = new window.ImageCapture(track);
              const photoCaps = await ic.getPhotoCapabilities?.();
              if (photoCaps) {
                if ("torch" in photoCaps) hasTorch = !!photoCaps.torch;
                else if (photoCaps.fillLightMode?.includes?.("torch")) hasTorch = true;
              }
            } catch {}
          }

          if (hasTorch) return { stream, track, hasTorch: true };

          // если torch нет — освобождаем и идём дальше
          stream.getTracks().forEach(t => t.stop());
        } catch {}
      }
    } catch {}

    // Фоллбек: обычный environment
    const fb = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: "environment" } },
    });
    return { stream: fb, track: fb.getVideoTracks()[0], hasTorch: false };
  };

  const tryTorch = async (on) => {
    const track = trackRef.current;
    if (!track) return false;
    const tryOne = async (c) => track.applyConstraints(c).then(() => true).catch(() => false);

    if (await tryOne({ advanced: [{ torch: on }] })) return true;
    if (await tryOne({ torch: on })) return true;

    if ("ImageCapture" in window) {
      try {
        const ic = new window.ImageCapture(track);
        await ic.setOptions({ torch: on });
        return true;
      } catch {}
    }
    return false;
  };

  const toggleTorch = async () => {
    const ok = await tryTorch(!torchOn);
    if (!ok) {
      setError("Torch не підтримується цією камерою/браузером");
      return;
    }
    setTorchOn(v => !v);
  };

  // -------- Старт/Стоп --------
  const startScan = useCallback(async () => {
    if (active) return;
    setError(null); setFires(0); setLastCode(""); setLog([]);
    lastByCodeRef.current.clear();

    try {
      // 0) подготовка <video> под автоплей iOS (до любых await)
      if (videoRef.current) {
        videoRef.current.setAttribute?.("playsinline", "");
        videoRef.current.setAttribute?.("webkit-playsinline", "");
        videoRef.current.muted = true;
      }

      // 1) СРАЗУ (внутри жеста) — ready-тон / play-beep → надёжный unlock на iOS
      playReadyTone();

      setActive(true);

      // дальше можно yield/await
      await new Promise(r => requestAnimationFrame(r));
      if (!videoRef.current) throw new Error("Video element not ready");

      // 2) камера
      let stream, track, hasTorch = false;

      if (!isIOS && !isMacDesktop) {
        const pick = await getRearStreamWithTorch();
        stream = pick.stream; track = pick.track; hasTorch = !!pick.hasTorch;
      } else {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
        track = stream.getVideoTracks()[0];
      }

      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      try { await videoRef.current.play(); } catch {}

      trackRef.current = track;
      await applyTrackControls(track);

      // 3) torch capability
      try {
        const caps = track.getCapabilities?.() || {};
        if (caps.torch) hasTorch = true;
      } catch {}
      setTorchCapable(hasTorch);
      setTorchOn(false);

      // 4) детектор
      let canNative = false;
      if ("BarcodeDetector" in window) {
        try {
          const want = ["ean_13","ean_8","upc_a","upc_e","code_128","qr_code","data_matrix","pdf417","aztec"];
          let formats = want;
          if (window.BarcodeDetector.getSupportedFormats) {
            const supported = await window.BarcodeDetector.getSupportedFormats();
            if (Array.isArray(supported) && supported.length) {
              const inter = supported.filter(f => want.includes(f));
              formats = inter.length ? inter : supported;
            }
          }
          detectorRef.current = new window.BarcodeDetector({ formats });
          canNative = true;
        } catch {
          canNative = false;
        }
      }

      if (canNative) {
        modeRef.current = "native";
      } else {
        // ZXing fallback
        const z = await import("@zxing/library");
        const hints = new Map();
        const formats = [
          z.BarcodeFormat.QR_CODE,
          z.BarcodeFormat.DATA_MATRIX,
          z.BarcodeFormat.PDF_417,
          z.BarcodeFormat.AZTEC,
          z.BarcodeFormat.EAN_13,
          z.BarcodeFormat.EAN_8,
          z.BarcodeFormat.UPC_A,
          z.BarcodeFormat.UPC_E,
          z.BarcodeFormat.CODE_128,
          z.BarcodeFormat.ITF,
          z.BarcodeFormat.CODE_39,
        ];
        hints.set(z.DecodeHintType.POSSIBLE_FORMATS, formats);
        const reader = new z.MultiFormatReader();
        reader.setHints(hints);

        zxingRef.current = {
          reader,
          HTMLCanvasElementLuminanceSource: z.HTMLCanvasElementLuminanceSource,
          HybridBinarizer: z.HybridBinarizer,
          BinaryBitmap: z.BinaryBitmap,
        };
        modeRef.current = "zxing";
      }

      // 5) цикл детекции
      startDetectLoop();
    } catch (e) {
      const msg = e?.message || String(e);
      setError(msg);
      setActive(false);
      if (/NotAllowedError|Permission|gesture|play\(\) request/i.test(msg)) {
        setNeedsGesture(true);
      }
    }
  }, [active, isIOS, isMacDesktop]);

  const stopScan = useCallback(() => {
    cancelFrameLoop();
    try {
      const s = streamRef.current || videoRef.current?.srcObject;
      if (s?.getTracks) s.getTracks().forEach((t) => t.stop());
    } catch {}
    try { if (videoRef.current) videoRef.current.srcObject = null; } catch {}

    streamRef.current = null;
    trackRef.current  = null;
    detectorRef.current = null;
    zxingRef.current = null;
    modeRef.current = "none";
    // canvas ctxRef пересоздастся при необходимости

    setTorchOn(false);
    setTorchCapable(false);
    setActive(false);
  }, []);

  // авто-пауза/рестарт при скрытии вкладки
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        cancelFrameLoop();
      } else if (active) {
        startDetectLoop();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [active]);

  useEffect(() => stopScan, [stopScan]);

  // Тап по видео — мягкий «пинок» автофокуса (если доступен)
  const onTap = async (e) => {
    const rect = videoRef.current?.getBoundingClientRect?.();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    const track = trackRef.current;
    try {
      const caps = track?.getCapabilities?.() || {};
      if (caps.pointsOfInterest) {
        await track.applyConstraints({ advanced: [{ pointsOfInterest: [{ x, y }], focusMode: "single-shot" }] });
        await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
      } else if (caps.focusMode?.includes?.("continuous")) {
        await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
      }
    } catch {}
  };

  const showTorchBtn = torchCapable;

  return (
    <div style={{ position: "relative" }}>
      {/* Видео + прицел */}
      <div
        onClick={onTap}
        style={{
          position: "relative",
          width: "100%",
          height,
          borderRadius: 12,
          overflow: "hidden",
          background: "#000",
          border: "1px solid #ccc",
          marginBottom: 8,
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        {/* ROI рамка */}
        <div style={{ pointerEvents: "none", position: "absolute", inset: 0 }}>
          <div
            style={{
              position: "absolute",
              left: "10%",
              right: "10%",
              top: "30%",
              bottom: "30%",
              border: "2px dashed rgba(255,255,255,0.9)",
              borderRadius: 8,
            }}
          />
        </div>
        {/* скан-линия */}
        {active && (
          <>
            <div
              style={{
                pointerEvents: "none",
                position: "absolute",
                left: "10%",
                right: "10%",
                height: 2,
                background: "rgba(255,255,255,0.9)",
                animation: "scanline 2.2s linear infinite",
              }}
            />
            <style>{`@keyframes scanline{0%{top:31%}50%{top:69%}100%{top:31%}}`}</style>
          </>
        )}
      </div>

      {/* Управление */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
        {!active ? (
          <Button onClick={() => { setNeedsGesture(false); startScan(); }}>
            {needsGesture ? "Почати сканування" : "Сканувати"}
          </Button>
        ) : (
          <>
            <Button type="primary" danger onClick={stopScan}>Завершити сканування</Button>
            {showTorchBtn && (
              <Button onClick={toggleTorch}>
                {torchOn ? "Вимкнути ліхтарик" : "Увімкнути ліхтарик"}
              </Button>
            )}
          </>
        )}
        {error && <span style={{ color: "red", fontSize: 12 }}>{error}</span>}
      </div>

      {/* Индикатор */}
      <div
        style={{
          border: "1px dashed #ddd",
          borderRadius: 8,
          padding: 10,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          background: "#fafafa",
        }}
      >
        <div style={{ marginBottom: 6, color: "#666", fontSize: 12 }}>
          Детектор:{" "}
          <b>{modeRef.current === "native" ? "BarcodeDetector" : modeRef.current === "zxing" ? "ZXing (fallback)" : "—"}</b>
          {" "}• Статус:{" "}
          <b style={{ color: active ? "#16a34a" : "#dc2626" }}>
            {active ? "сканую…" : "зупинено"}
          </b>
        </div>
        <div style={{ marginBottom: 6 }}>
          Останній код:{" "}
          <code style={{ fontSize: 16, fontWeight: 600 }}>
            {lastCode || "—"}
          </code>
        </div>
        <div style={{ marginBottom: 6 }}>
          Віддано назовні (<code>onScan</code>): <b>{fires}</b>
          {beep ? " • звук: увімкнено" : " • звук: вимкнено"}
        </div>
        <div>
          Лог (10 останніх):
          <ol style={{ margin: "6px 0 0 20px", padding: 0 }}>
            {log.length === 0 ? (
              <li style={{ color: "#999" }}>—</li>
            ) : (
              log.map((it, i) => (
                <li key={i}>
                  <code>{it.text}</code>{" "}
                  <small style={{ color: "#999" }}>
                    {new Date(it.ts).toLocaleTimeString()}
                  </small>
                </li>
              ))
            )}
          </ol>
        </div>
      </div>
    </div>
  );
}
